/**
 * ステージ（地域）選択モーダル。
 *
 * 今は福岡ひとつだが、**これが全国マップの土台**になる。
 * 各ステージの図鑑の進みとハイスコアを、ステージ別の保存領域から読んで並べる。
 *
 * ステージの切り替えは `location.assign()` による通常の遷移にしている。
 * Phaser のシーンとアセットを張り替えるより、読み込み直すほうが確実で、
 * URL が素直に変わるぶん共有も解析も正しくなる。
 */

import { stageStorageKey } from '../game/config/gameConfig';
import { idMigrationFor } from '../game/config/idMigrations';
import { STAGES, stagePath } from '../game/config/stages';
import { StorageSystem, type StorageLike } from '../game/systems/StorageSystem';

import { groupByArea } from './stageGrouping';
import { VisitedStore, decorateVisitedRows } from './visitedMarks';

export interface StageSummary {
  id: string;
  /** 例: '北海道' */
  regionName: string;
  title: string;
  /** 収録数。 */
  itemCount: number;
  /** 図鑑で解放済みの数。 */
  unlockedCount: number;
  highScore: number;
  /** 遊んだ回数（のんびりモードだけの県もここで「あそんだ」になる）。 */
  playCount: number;
  /** ご当地ステージ（親を持つ）かどうか。制覇率の分母から外す目印。 */
  isLocal: boolean;
}

/**
 * 全ステージの進み具合を集める。
 *
 * 保存領域を読むだけで、ステージ定義そのものは変更しない。
 */
export function collectStageSummaries(storage?: StorageLike | null): StageSummary[] {
  // draft のステージ（絵がまだ揃っていない県）は一覧に出さない。
  // 直リンクでは開けるので、自分で下見はできる。
  return Object.entries(STAGES)
    .filter(([, stage]) => !stage.draft)
    .map(([id, stage]) => {
      const save = new StorageSystem({
        key: stageStorageKey(id),
        storage,
        idMigration: idMigrationFor(id),
      });
      return {
        id,
        regionName: stage.regionName,
        title: stage.title,
        itemCount: stage.items.length,
        // いまの県データに無いID（差し替えで外した料理など）は数えない。12品なのに 13 と出ないように
        unlockedCount: save.unlockedItemIds.filter((itemId) =>
          stage.items.some((item) => item.id === itemId),
        ).length,
        highScore: save.highScore,
        playCount: save.playCount,
        isLocal: Boolean(stage.parentId),
      };
    });
}

/** 一覧の右端に出す文字。のんびりモードだけ遊んだ県は点がなくても「あそんだ」。 */
export function scoreLabel(summary: StageSummary): string {
  if (summary.highScore > 0) return `${summary.highScore.toLocaleString('ja-JP')} 点`;
  return summary.playCount > 0 ? 'あそんだ' : '未プレイ';
}

/** 遊んだことがあるか。 */
export function hasPlayed(summary: StageSummary): boolean {
  return summary.playCount > 0 || summary.highScore > 0;
}

/** 制覇率（ご当地ステージは分母に入れない）。 */
export function clearRate(summaries: readonly StageSummary[]): {
  cleared: number;
  total: number;
} {
  const target = summaries.filter((s) => !s.isLocal);
  const cleared = target.filter((s) => s.itemCount > 0 && s.unlockedCount >= s.itemCount).length;
  return { cleared, total: target.length };
}

export class StageSelectModal {
  private readonly root: HTMLElement | null;
  private readonly listEl: HTMLElement | null;
  private readonly progressEl: HTMLElement | null;
  private readonly closeBtn: HTMLButtonElement | null;
  private readonly currentStageId: string;

  constructor(currentStageId: string, root: ParentNode = document) {
    this.currentStageId = currentStageId;
    this.root = root.querySelector<HTMLElement>('[data-ui="stage-modal"]');
    this.listEl = root.querySelector<HTMLElement>('[data-ui="stage-list"]');
    this.progressEl = root.querySelector<HTMLElement>('[data-ui="stage-progress"]');
    this.closeBtn = root.querySelector<HTMLButtonElement>('[data-ui="stage-close"]');

    this.closeBtn?.addEventListener('click', () => this.hide());
    root
      .querySelector<HTMLElement>('[data-ui="stage-backdrop"]')
      ?.addEventListener('click', () => this.hide());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) this.hide();
    });
  }

  /** モーダルを閉じたときに呼ばれる。 */
  onClose?: () => void;

  open(): void {
    if (!this.root) return;
    this.render();
    this.root.dataset.visible = 'true';
    this.closeBtn?.focus();
  }

  hide(): void {
    if (!this.root) return;
    this.root.dataset.visible = 'false';
    this.onClose?.();
  }

  isOpen(): boolean {
    return this.root?.dataset.visible === 'true';
  }

  private render(): void {
    if (!this.listEl) return;
    const summaries = collectStageSummaries();

    if (this.progressEl) {
      const { cleared, total } = clearRate(summaries);
      this.progressEl.textContent = `制覇 ${cleared} / ${total}`;
    }

    this.listEl.replaceChildren(...this.buildGroups(summaries));
    // 行ったことのある県の印（端末に保存。トロフィーには数えない）
    decorateVisitedRows(this.listEl, '.stage-item', new VisitedStore());
  }

  /**
   * 地方ごとにまとめて並べる。
   *
   * 11県でも箇条書きは読みにくく、47県では成立しない。
   * 並びの決まりごとは `stageGrouping.ts` に置いてあり、トップの県選びと共通。
   */
  private buildGroups(summaries: StageSummary[]): HTMLElement[] {
    const nodes: HTMLElement[] = [];
    for (const group of groupByArea(summaries)) {
      nodes.push(this.buildAreaHeading(group.name, group.items));
      nodes.push(...group.items.map((summary) => this.buildRow(summary)));
    }
    return nodes;
  }

  /** 地方の見出し。その地方で何県遊べるかを添える。 */
  private buildAreaHeading(name: string, list: StageSummary[]): HTMLElement {
    const heading = document.createElement('div');
    heading.className = 'stage-area';

    const label = document.createElement('span');
    label.className = 'stage-area__name';
    label.textContent = name;

    const count = document.createElement('span');
    count.className = 'stage-area__count';
    const played = list.filter((s) => s.playCount > 0 || s.highScore > 0).length;
    count.textContent = `${played} / ${list.length}`;

    heading.append(label, count);
    return heading;
  }

  private buildRow(summary: StageSummary): HTMLElement {
    const isCurrent = summary.id === this.currentStageId;
    const row = document.createElement(isCurrent ? 'div' : 'button');
    row.className = 'stage-item';
    row.dataset.region = STAGES[summary.id]?.regionId ?? '';
    row.dataset.current = isCurrent ? 'true' : 'false';
    if (row instanceof HTMLButtonElement) {
      row.type = 'button';
      row.addEventListener('click', () => {
        location.assign(stagePath(summary.id));
      });
    }

    const name = document.createElement('span');
    name.className = 'stage-item__name';
    name.textContent = summary.regionName;

    const meta = document.createElement('span');
    meta.className = 'stage-item__meta';
    meta.textContent = isCurrent
      ? `図鑑 ${summary.unlockedCount} / ${summary.itemCount} ・ 今ここ`
      : `図鑑 ${summary.unlockedCount} / ${summary.itemCount}`;

    const score = document.createElement('span');
    score.className = 'stage-item__score';
    score.textContent = scoreLabel(summary);

    row.append(name, meta, score);
    return row;
  }
}
